// MyConnectionsPanel — slide-over listing the caller's REAL ecosystem
// connections (getEcosystemConnections → GET /ecosystem/connections), sent
// via the same Connect action as ConnectModal. Honest empty state when
// there are none — never a fabricated count or list.
import { useEffect, useState } from 'react'
import { X, Users, Loader2, ArrowUpRight, ArrowDownLeft } from 'lucide-react'
import { getEcosystemConnections } from '../../services/api'
import type { EcosystemConnection } from '../../types'
import EmptyState from '../ui/EmptyState'

interface MyConnectionsPanelProps {
  onClose: () => void
}

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-gray-100 text-gray-600',
}

export default function MyConnectionsPanel({ onClose }: MyConnectionsPanelProps) {
  const [requests, setRequests] = useState<EcosystemConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    getEcosystemConnections()
      .then((r) => {
        if (!cancelled) setRequests(r)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="My connections">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-5">
          <h3 className="flex items-center gap-2 text-base font-semibold text-iflow-900">
            <Users className="w-4 h-4" /> My Connections
          </h3>
          <button onClick={onClose} aria-label="Close" className="text-gray-500 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your connection requests…
            </div>
          ) : error ? (
            <p className="text-sm text-red-600">Could not load your connections. Please try again.</p>
          ) : requests.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No connection requests yet"
              description="When you send or receive a connection request, it will show up here."
              compact
            />
          ) : (
            <ul className="space-y-3">
              {requests.map((r) => (
                <li key={r.connection_id} className="rounded-lg border border-gray-200 p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                      {r.direction === 'received' ? (
                        <ArrowDownLeft className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      )}
                      {r.direction === 'received' ? 'Received' : 'Sent'}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>
                      {r.status}
                    </span>
                  </div>
                  {r.message && <p className="line-clamp-2 text-sm text-gray-600">{r.message}</p>}
                  <p className="mt-1 text-[10px] text-gray-400">{new Date(r.created_at).toLocaleDateString()}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
